<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { type ControlSize, control } from "@/design/control";
import {
	COUNTRIES,
	type Country,
	countryOf,
	flagOf,
	joinPhone,
	maskPhone,
	nameOf,
	splitPhone,
} from "@/design/phone";
import Field from "./Field.vue";
import Icon from "./Icon.vue";
import Popover from "./Popover.vue";

/**
 * Um telefone: a bandeira do país, e o resto.
 *
 * ── O QUE ISTO SUBSTITUI ──────────────────────────────────────────────────
 *
 * Um `TextField type="tel"` e uma regra escondida: o servidor guarda E.164 e
 * RECUSA um número nacional — `11987654321` é um celular de São Paulo e também
 * um número dos EUA, e adivinhar seria escolher o cliente errado uma vez por
 * mês. A recusa está certa; o que estava errado era quem pagava por ela. Quem
 * cadastra digitava o número como o lê no WhatsApp e levava "informe o código do
 * país", uma frase que descreve um formato de armazenamento a quem só queria
 * anotar um telefone. A V2 nunca teve esse problema: lá o país é uma bandeira
 * que já vem escolhida.
 *
 * Aqui o país é um botão com a bandeira e o DDI, o campo recebe só a parte
 * local, e o `+55` é COSTURADO na saída. Não há como esquecê-lo, então não há
 * mais o que recusar.
 *
 * ── O QUE ELE EMITE ───────────────────────────────────────────────────────
 *
 * E.164 com o `+` — `+5511987654321` — ou string vazia. O mesmo que o campo
 * anterior emitia quando dava certo, para que trocar um pelo outro não mexa em
 * nenhuma validação, nenhum DTO e nenhuma coluna.
 *
 * ── POR QUE NÃO É UM WRAPPER DO `TextField` ───────────────────────────────
 *
 * Porque o `TextField` desenha UM controle dentro do envelope, e aqui são dois
 * que têm de parecer um só: a moldura é do conjunto, e o foco em qualquer metade
 * acende a moldura inteira. Os dois usam o mesmo `Field` e a mesma receita de
 * `control.ts`, que é onde a semelhança tem de morar.
 */
defineOptions({ inheritAttrs: false });

const props = withDefaults(
	defineProps<{
		/** E.164 com `+`, ou vazio. */
		modelValue: string;
		label?: string;
		size?: ControlSize;
		hint?: string;
		error?: string;
		invalid?: boolean;
		describedBy?: string;
		id?: string;
		required?: boolean;
		disabled?: boolean;
		/** O país que vem escolhido quando o valor está vazio. */
		defaultCountry?: string;
		placeholder?: string;
		class?: string;
	}>(),
	{ defaultCountry: "BR" },
);

const emit = defineEmits<{ "update:modelValue": [string] }>();

const { t, locale } = useI18n({ useScope: "global" });

const country = ref<Country>(countryOf(props.defaultCountry));
const local = ref("");

/**
 * O último valor que ESTE campo emitiu.
 *
 * Sem ele, o `v-model` de duas vias devolve o que acabámos de emitir, o watcher
 * re-parte a string e o DDI entra outra vez na parte local a cada tecla: digitar
 * "1" vira "551", depois "5512", depois "555512". O eco do próprio valor é o
 * único que se ignora — um valor vazio vindo de fora (um formulário que se
 * limpou) tem de limpar mesmo.
 */
const lastEmitted = ref("");

watch(
	() => props.modelValue,
	(value) => {
		if (value && value === lastEmitted.value) return;
		const parsed = splitPhone(value, props.defaultCountry);
		country.value = parsed.country;
		local.value = parsed.local;
	},
	{ immediate: true },
);

const masked = computed(() => maskPhone(country.value, local.value));

/** O que o campo mostra vazio: a própria máscara, com zeros. */
const hollow = computed(() => props.placeholder ?? country.value.mask.replace(/#/g, "0"));

function publish(): void {
	const value = joinPhone(country.value, local.value);
	lastEmitted.value = value;
	emit("update:modelValue", value);
}

function type(event: Event): void {
	const typed = (event.target as HTMLInputElement).value.replace(/\D/g, "");
	// Mais um do que o país pede, e não mais: é a folga do nono dígito, e sem o
	// teto quem cola um número com DDI empurra dígitos para dentro da máscara.
	local.value = typed.slice(0, country.value.localLength + 1);
	publish();
}

const picking = ref(false);
const search = ref("");

const offered = computed(() => {
	const term = search.value.trim().toLowerCase();
	const all = COUNTRIES.map((c) => ({ ...c, name: nameOf(c.iso, locale.value) }));
	if (!term) return all;
	return all.filter(
		(c) =>
			c.name.toLowerCase().includes(term) ||
			c.dial.includes(term.replace("+", "")) ||
			c.iso.toLowerCase().includes(term),
	);
});

function choose(chosen: Country): void {
	country.value = countryOf(chosen.iso);
	picking.value = false;
	search.value = "";
	publish();
}

/**
 * A moldura é do conjunto e não de cada metade: `control()` traz a borda, o
 * fundo e a altura, e o `focus-within` acende quando o foco está em qualquer uma
 * das duas. Sem padding lateral, porque quem o tem são os filhos.
 */
const frame = computed(() =>
	twMerge(
		control({ size: props.size }),
		"flex items-stretch overflow-hidden p-0 focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/20",
		props.class,
	),
);
</script>

<template>
	<Field
		v-slot="{ id: controlId, describedBy: describes, invalid: wrong }"
		:label="label"
		:hint="hint"
		:error="error"
		:required="required"
		:invalid="invalid"
		:described-by="describedBy"
		:id="id"
	>
		<div :class="frame">
			<Popover
				:open="picking"
				:label="t('phone.pickCountry')"
				panel-class="w-72"
				@update:open="picking = $event"
			>
				<template #trigger>
					<button
						type="button"
						class="flex shrink-0 items-center gap-1.5 border-border border-r px-2.5 text-fg-muted text-sm transition-colors hover:bg-bg-muted disabled:cursor-not-allowed"
						:disabled="disabled"
						:aria-label="t('phone.pickCountry')"
						:title="`${nameOf(country.iso, locale)} (+${country.dial})`"
						:aria-expanded="picking"
					>
						<span class="text-base leading-none">{{ flagOf(country.iso) }}</span>
						<span class="font-medium tabular-nums leading-none">+{{ country.dial }}</span>
						<Icon name="chevronDown" class="size-3 opacity-50" />
					</button>
				</template>

				<div class="flex max-h-80 flex-col">
					<div class="border-border border-b p-2">
						<input
							v-model="search"
							type="search"
							class="h-8 w-full rounded-lg border border-border-strong bg-surface-raised px-2.5 text-fg text-sm placeholder:text-fg-subtle focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/20"
							:placeholder="t('phone.searchCountry')"
						/>
					</div>
					<div class="min-h-0 flex-1 overflow-y-auto p-1">
						<button
							v-for="option in offered"
							:key="`${option.iso}-${option.dial}`"
							type="button"
							class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm transition-colors"
							:class="
								option.iso === country.iso
									? 'bg-accent-subtle font-medium text-accent'
									: 'text-fg hover:bg-bg-muted'
							"
							@click="choose(option)"
						>
							<span class="text-base leading-none">{{ flagOf(option.iso) }}</span>
							<span class="min-w-0 flex-1 truncate">{{ option.name }}</span>
							<span class="text-fg-subtle tabular-nums">+{{ option.dial }}</span>
						</button>
						<p v-if="offered.length === 0" class="px-2.5 py-4 text-center text-fg-subtle text-sm">
							{{ t("phone.noCountry") }}
						</p>
					</div>
				</div>
			</Popover>

			<input
				:id="controlId"
				type="tel"
				inputmode="numeric"
				autocomplete="tel-national"
				class="min-w-0 flex-1 border-none bg-transparent px-3 text-fg text-sm placeholder:text-fg-subtle focus:outline-none disabled:cursor-not-allowed"
				:value="masked"
				:placeholder="hollow"
				:required="required"
				:disabled="disabled"
				:aria-invalid="wrong"
				:aria-describedby="describes"
				v-bind="$attrs"
				@input="type"
			/>
		</div>
	</Field>
</template>
